/**
 * Error simulation utilities for mock data
 * Matches A55 error structure with query param triggers
 */

import { HttpResponse } from 'msw'

/**
 * A55 API Error structure
 */
export interface A55Error {
  code: string
  message: string
  details?: Record<string, unknown>
  timestamp: string
  path?: string
  requestId?: string
}

export interface A55ErrorResponse {
  error: A55Error
}

/**
 * Standard HTTP error codes
 */
export const HTTP_ERRORS = {
  400: { code: 'BAD_REQUEST', message: 'Invalid request parameters' },
  401: { code: 'UNAUTHORIZED', message: 'Authentication required' },
  403: { code: 'FORBIDDEN', message: 'Access denied' },
  404: { code: 'NOT_FOUND', message: 'Resource not found' },
  409: { code: 'CONFLICT', message: 'Resource conflict' },
  422: { code: 'UNPROCESSABLE_ENTITY', message: 'Validation failed' },
  429: { code: 'TOO_MANY_REQUESTS', message: 'Rate limit exceeded' },
  500: { code: 'INTERNAL_ERROR', message: 'Internal server error' },
  502: { code: 'BAD_GATEWAY', message: 'Bad gateway' },
  503: { code: 'SERVICE_UNAVAILABLE', message: 'Service temporarily unavailable' },
  504: { code: 'GATEWAY_TIMEOUT', message: 'Gateway timeout' }
} as const

export type HttpErrorCode = keyof typeof HTTP_ERRORS

/**
 * A55-specific error codes
 */
export const A55_ERROR_CODES = {
  INVALID_MERCHANT: { code: 'A55_INVALID_MERCHANT', message: 'Invalid merchant identifier', status: 400 },
  INVALID_WALLET: { code: 'A55_INVALID_WALLET', message: 'Invalid wallet identifier', status: 400 },
  INVALID_TRANSACTION: { code: 'A55_INVALID_TRANSACTION', message: 'Invalid transaction identifier', status: 400 },
  MERCHANT_NOT_FOUND: { code: 'A55_MERCHANT_NOT_FOUND', message: 'Merchant not found', status: 404 },
  WALLET_NOT_FOUND: { code: 'A55_WALLET_NOT_FOUND', message: 'Wallet not found', status: 404 },
  TRANSACTION_NOT_FOUND: { code: 'A55_TRANSACTION_NOT_FOUND', message: 'Transaction not found', status: 404 },
  INSUFFICIENT_FUNDS: { code: 'A55_INSUFFICIENT_FUNDS', message: 'Insufficient wallet funds', status: 422 },
  DUPLICATE_TRANSACTION: { code: 'A55_DUPLICATE_TRANSACTION', message: 'Duplicate transaction detected', status: 409 },
  FRAUD_DETECTED: { code: 'A55_FRAUD_DETECTED', message: 'Transaction blocked due to fraud detection', status: 403 },
  LIMIT_EXCEEDED: { code: 'A55_LIMIT_EXCEEDED', message: 'Transaction limit exceeded', status: 422 },
  INVALID_CARD: { code: 'A55_INVALID_CARD', message: 'Invalid card information', status: 400 },
  CARD_BLOCKED: { code: 'A55_CARD_BLOCKED', message: 'Card is blocked', status: 403 },
  EXPIRED_CARD: { code: 'A55_EXPIRED_CARD', message: 'Card has expired', status: 400 }
} as const

export type A55ErrorCode = keyof typeof A55_ERROR_CODES

/**
 * Configuration for error simulation
 */
export interface ErrorSimulationConfig {
  /** Enable random error simulation */
  enabled: boolean
  /** Error rate (0-1, e.g., 0.05 = 5% of requests fail) */
  errorRate: number
  /** Specific error codes to simulate (if empty, uses random HTTP errors) */
  errorCodes?: HttpErrorCode[]
}

const DEFAULT_ERROR_CONFIG: ErrorSimulationConfig = {
  enabled: false,
  errorRate: 0.05,
  errorCodes: [500, 502, 503, 504]
}

/**
 * Generate a unique request ID
 */
function generateRequestId(): string {
  return `req_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}

/**
 * Build an A55-compatible error response
 */
export function buildErrorResponse(
  status: HttpErrorCode,
  options: Partial<Omit<A55Error, 'timestamp'>> = {}
): A55ErrorResponse {
  const baseError = HTTP_ERRORS[status]

  return {
    error: {
      code: options.code || baseError.code,
      message: options.message || baseError.message,
      details: options.details,
      timestamp: new Date().toISOString(),
      path: options.path,
      requestId: options.requestId || generateRequestId()
    }
  }
}

/**
 * Build an A55-specific error response
 */
export function buildA55ErrorResponse(
  errorCode: A55ErrorCode,
  options: { path?: string, details?: Record<string, unknown> } = {}
): { status: number, response: A55ErrorResponse } {
  const errorDef = A55_ERROR_CODES[errorCode]

  return {
    status: errorDef.status,
    response: {
      error: {
        code: errorDef.code,
        message: errorDef.message,
        details: options.details,
        timestamp: new Date().toISOString(),
        path: options.path,
        requestId: generateRequestId()
      }
    }
  }
}

/**
 * Create an MSW HttpResponse for an error
 */
export function createErrorResponse(
  status: HttpErrorCode,
  options: Partial<Omit<A55Error, 'timestamp'>> = {}
): ReturnType<typeof HttpResponse.json> {
  const errorResponse = buildErrorResponse(status, options)

  return HttpResponse.json(errorResponse, { status })
}

/**
 * Create an MSW HttpResponse for an A55 error
 */
export function createA55ErrorResponse(
  errorCode: A55ErrorCode,
  options: { path?: string, details?: Record<string, unknown> } = {}
): ReturnType<typeof HttpResponse.json> {
  const { status, response } = buildA55ErrorResponse(errorCode, options)

  return HttpResponse.json(response, { status })
}

/**
 * Parse error simulation parameters from URL
 * Supports: ?_error=500 or ?_error=NOT_FOUND or ?_error=A55_MERCHANT_NOT_FOUND
 */
export function parseErrorParam(searchParams: URLSearchParams): {
  shouldError: boolean
  httpStatus?: HttpErrorCode
  a55Error?: A55ErrorCode
} {
  const errorParam = searchParams.get('_error')

  if (!errorParam) {
    return { shouldError: false }
  }

  // Check if it's a numeric HTTP status
  const numericStatus = Number.parseInt(errorParam, 10)
  if (!Number.isNaN(numericStatus) && numericStatus in HTTP_ERRORS) {
    return { shouldError: true, httpStatus: numericStatus as HttpErrorCode }
  }

  // Check if it's an A55 error code
  if (errorParam in A55_ERROR_CODES) {
    return { shouldError: true, a55Error: errorParam as A55ErrorCode }
  }

  // Check if it matches an HTTP error code name
  const httpErrorKey = Object.keys(HTTP_ERRORS).find(
    key => HTTP_ERRORS[Number(key) as HttpErrorCode].code === errorParam
  )
  if (httpErrorKey) {
    return { shouldError: true, httpStatus: Number(httpErrorKey) as HttpErrorCode }
  }

  return { shouldError: false }
}

/**
 * Simulate random errors based on configuration
 */
export function simulateRandomError(
  config: Partial<ErrorSimulationConfig> = {}
): { shouldError: boolean, status?: HttpErrorCode } {
  const mergedConfig = { ...DEFAULT_ERROR_CONFIG, ...config }

  if (!mergedConfig.enabled) {
    return { shouldError: false }
  }

  if (Math.random() > mergedConfig.errorRate) {
    return { shouldError: false }
  }

  // Pick a random error code
  const errorCodes = mergedConfig.errorCodes || [500]
  const status = errorCodes[Math.floor(Math.random() * errorCodes.length)]!

  return { shouldError: true, status }
}

/**
 * Handle error simulation for a request
 * Returns an error response if simulation triggers, otherwise null
 */
export function handleErrorSimulation(
  searchParams: URLSearchParams,
  config: Partial<ErrorSimulationConfig> = {},
  path?: string
): ReturnType<typeof HttpResponse.json> | null {
  // Check for explicit error trigger first
  const explicitError = parseErrorParam(searchParams)

  if (explicitError.shouldError) {
    if (explicitError.a55Error) {
      return createA55ErrorResponse(explicitError.a55Error, { path })
    }
    if (explicitError.httpStatus) {
      return createErrorResponse(explicitError.httpStatus, { path })
    }
  }

  // Check for random error simulation
  const randomError = simulateRandomError(config)
  if (randomError.shouldError && randomError.status) {
    return createErrorResponse(randomError.status, { path })
  }

  return null
}

/**
 * Create a 404 Not Found response
 */
export function createNotFoundResponse(
  resourceType: string,
  resourceId: string,
  path?: string
): ReturnType<typeof HttpResponse.json> {
  return createErrorResponse(404, {
    message: `${resourceType} not found: ${resourceId}`,
    path,
    details: { resourceType, resourceId }
  })
}

/**
 * Create a 400 Bad Request response for validation errors
 */
export function createValidationErrorResponse(
  errors: Array<{ field: string, message: string }>,
  path?: string
): ReturnType<typeof HttpResponse.json> {
  return createErrorResponse(400, {
    code: 'VALIDATION_ERROR',
    message: 'Request validation failed',
    path,
    details: { errors }
  })
}
